import {
  Command,
  EffectStackExecutor,
  EffectStackExecutorProps,
  effectStackExecutorFactory,
  expectNever,
} from "~/utils";
import { Choice } from "./Choice";
import { Game } from "./Game";
import {
  AgentEffectChoices,
  AgentEffectDescription,
  AgentEffectFunctions,
  AgentEffectResult,
  EffectChoice,
  EffectChoiceResult,
} from "./effect";

type ValueProps = Command<typeof AgentEffectFunctions>;

type Context = {
  playerId: string;
  cardId?: string;
  actionSlotId?: string;
  techId?: string;
};

type Effect = AgentEffectDescription;

export type EffectExecution = EffectStackExecutorProps<
  Effect,
  EffectChoice[],
  Context
>;

export const EffectExecution = {
  new() {
    return effectStackExecutorFactory<Effect, EffectChoice[], Context>();
  },
};

export const EffectExecutionManager = (
  effectExecution: EffectExecution,
  game: Game
) => {
  const executor = EffectStackExecutor<ValueProps, {}, EffectChoice[], Context>(
    effectExecution,
    (valuePropotision, context) => {
      const { playerId, cardId, actionSlotId, techId } = context;
      const player = game.getPlayer(playerId);
      const card = cardId ? game.findCard(cardId) : undefined;
      const slot = actionSlotId ? game.getActionSlot(actionSlotId) : undefined;
      const techTile = techId ? game.getTechTile(techId, true) : undefined;
      const callResult = Command.call(
        AgentEffectFunctions,
        valuePropotision
      ).apply({
        game,
        player,
        card,
        slot,
        techTile,
      });
      if (AgentEffectResult.isBoolean(callResult)) {
        return callResult ? "passing" : "notPassing";
      } else if (AgentEffectResult.isChoice(callResult)) {
        if (callResult.length) {
          return callResult.map((newChoice) => Choice.new(newChoice));
        } else {
          return "passing";
        }
      } else if (AgentEffectResult.isEffect(callResult)) {
        executor.add(callResult.effect, context);
        return "passing";
      } else {
        expectNever(callResult);
        return "passing";
      }
    }
  );

  const getNextBlockingResult = (): EffectChoice[] => {
    const exec = executor.getCurrentExec();
    if (!exec) return [];
    const { blockResult = [] } = exec;
    return blockResult;
  };

  const getNextChoiceForPlayer = (playerId: string) => {
    return getNextBlockingResult().find(
      (choice) => choice.playerId === playerId
    );
  };

  return {
    ...executor,
    answer(result: EffectChoiceResult, playerId: string) {
      const choice = getNextChoiceForPlayer(playerId);
      if (!choice) {
        throw new Error(`could not find choice for player id ${playerId}`);
      }
      if (Choice.isAnswered(choice)) {
        throw new Error(
          `Cannot answer choice ${JSON.stringify(
            choice
          )} : choice is already answered. `
        );
      }
      choice.results.push(result);

      if (Choice.isAnswered(choice)) {
        const { type, results } = choice;
        const blockResult = getNextBlockingResult();
        if (blockResult.every((choice) => Choice.isAnswered(choice))) {
          const totalPassing = blockResult.reduce((acc, choice) => {
            const isNotSkipping = choice.results.every(({ skip }) => !skip);
            const isRecycleIntrigueCards =
              choice.type === "discardIntrigueCard" &&
              choice.choiceCount === 9999;
            const isPassing = isNotSkipping || isRecycleIntrigueCards;
            return acc && isPassing;
          }, true);
          executor.answerBlockingResult(
            totalPassing ? "passing" : "notPassing"
          );
        }
        const functions = results.flatMap((result) => {
          if (result.skip) {
            return [];
          }
          const callResult = Command.call(AgentEffectChoices, {
            methodName: type,
            arg: result.value,
          });
          return [callResult];
        });

        const player = game.getPlayer(playerId);
        functions.forEach((fct) => {
          const result = fct.apply({ game, player });
          if (AgentEffectResult.isEffect(result)) {
            executor.add(result.effect, { playerId });
          }
        });
        executor.executeAll();
      }
      return choice;
    },
    getNextChoiceForPlayer,
    getEffectContext() {
      const execStack = executor.getCurrentStack();
      if (!execStack) return;
      return execStack[0].effect;
    },
  };
};
